import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { format } from 'date-fns';
import clsx from 'clsx';
import { FiRefreshCw, FiActivity, FiServer, FiDatabase, FiCpu } from 'react-icons/fi';
import { statusApi } from '../../services/api';
import { SystemMetric } from '../../types';

interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'stable';
  isAboveThreshold?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit,
  icon,
  isAboveThreshold,
}) => (
  <div
    className={clsx(
      'bg-white rounded-xl shadow-sm border p-6',
      isAboveThreshold ? 'border-red-200' : 'border-gray-200'
    )}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            isAboveThreshold ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
          )}
        >
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {value}
            {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
          </p>
        </div>
      </div>
      {isAboveThreshold && (
        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
          Above threshold
        </span>
      )}
    </div>
  </div>
);

const MetricsViewer: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d'>('1h');
  const [selectedMetric, setSelectedMetric] = useState<string>('memory_usage');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const timeRangeHours: Record<string, number> = {
    '1h': 1,
    '6h': 6,
    '24h': 24,
    '7d': 168,
  };

  const fetchMetrics = async () => {
    setIsLoading(true);
    try {
      const data = await statusApi.getMetrics({ hours: timeRangeHours[timeRange] });
      setMetrics(data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [timeRange]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchMetrics();
    setIsRefreshing(false);
  };

  // Group metrics by type
  const metricTypes = Array.from(new Set(metrics.map(m => m.metricType)));

  // Get latest values for each metric
  const getLatestValue = (type: string, name: string): SystemMetric | undefined => {
    return metrics
      .filter(m => m.metricType === type && m.metricName === name)
      .sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime())[0];
  };

  // Prepare chart data
  const chartData = metrics
    .filter(m => m.metricType === selectedMetric)
    .map(m => ({
      time: format(new Date(m.recordedAt), 'HH:mm'),
      value: m.value,
      name: m.metricName,
    }))
    .reverse();

  // Get unique metric names for selected type
  const metricNames = Array.from(
    new Set(metrics.filter(m => m.metricType === selectedMetric).map(m => m.metricName))
  );

  if (isLoading && metrics.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">System Metrics</h1>
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 h-24"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">System Metrics</h1>
        <div className="flex items-center gap-4">
          {/* Time range selector */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg">
            {(['1h', '6h', '24h', '7d'] as const).map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setTimeRange(range)}
                className={clsx(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  timeRange === range
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                {range}
              </button>
            ))}
          </div>

          {/* Refresh button */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <FiRefreshCw className={clsx('w-5 h-5', isRefreshing && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Key metrics cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Heap Memory"
          value={getLatestValue('memory_usage', 'heap_alloc_mb')?.value.toFixed(1) ?? '--'}
          unit="MB"
          icon={<FiServer className="w-5 h-5" />}
          isAboveThreshold={getLatestValue('memory_usage', 'heap_alloc_mb')?.isAboveThreshold}
        />
        <MetricCard
          title="Goroutines"
          value={getLatestValue('cpu_usage', 'goroutine_count')?.value.toFixed(0) ?? '--'}
          icon={<FiCpu className="w-5 h-5" />}
          isAboveThreshold={getLatestValue('cpu_usage', 'goroutine_count')?.isAboveThreshold}
        />
        <MetricCard
          title="DB Connections"
          value={getLatestValue('database_health', 'open_connections')?.value ?? '--'}
          icon={<FiDatabase className="w-5 h-5" />}
          isAboveThreshold={getLatestValue('database_health', 'open_connections')?.isAboveThreshold}
        />
        <MetricCard
          title="DB Latency"
          value={getLatestValue('database_health', 'ping_latency_ms')?.value.toFixed(0) ?? '--'}
          unit="ms"
          icon={<FiActivity className="w-5 h-5" />}
          isAboveThreshold={getLatestValue('database_health', 'ping_latency_ms')?.isAboveThreshold}
        />
      </div>

      {/* Metric type selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Metric History</h2>
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            {metricTypes.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        {/* Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#0ea5e9"
                strokeWidth={2}
                fill="url(#colorValue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Metric names legend */}
        {metricNames.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {metricNames.map((name) => (
              <span
                key={name}
                className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
              >
                {name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsViewer;
